import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";
import DurationService from "app/services/DurationService";

import {
    ITEMS_PER_PAGE,
    DEFAULT_SORT_COLUMN,
    DEFAULT_SORT_ORDER,
    DEFAULT_TIME_GRANULARITY,
    DEFAULT_RADIO_FILTERS,
    FILTER_PATTERNS,
} from "../../../constants";
import type { IFoldersFilters } from "../../../types";

export const useFoldersConfig = () => {
    const { params } = useCurrentStateAndParams();
    const isCompare = isCompareMode();
    const keysArr = isCompare ? params?.key?.split(",") : [params?.key];

    const [currentDomain, setCurrentDomain] = useState<string>(keysArr[0]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: ITEMS_PER_PAGE });
    const [sortConfig, setSortConfig] = useState({
        columnKey: DEFAULT_SORT_COLUMN,
        order: DEFAULT_SORT_ORDER as "ascend" | "descend",
    });
    const [searchTerm, setSearchTerm] = useState<string>("");
    const getInitialRadioFilters = () => {
        if (params.isNew === "true") {
            return { isAll: false, isNew: true, isTrending: false };
        }
        if (params.isTrending === "true") {
            return { isAll: false, isNew: false, isTrending: true };
        }
        return DEFAULT_RADIO_FILTERS;
    };

    const [radioFilters, setRadioFilters] = useState(getInitialRadioFilters());

    const durationApi = DurationService.getDurationData(params.duration, params.comparedDuration);
    const filterString = searchTerm.trim() ? `Folder;contains;"${searchTerm.trim()}"` : "";

    const filters: IFoldersFilters = {
        country: params.country,
        from: durationApi.forAPI.from,
        to: durationApi.forAPI.to,
        includeSubDomains: false,
        isWindow: durationApi?.forAPI?.isWindow,
        keys: currentDomain,
        webSource: params.webSource,
        orderBy: `${sortConfig.columnKey} ${sortConfig.order === "ascend" ? "asc" : "desc"}`,
        timeGranularity: DEFAULT_TIME_GRANULARITY,
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        FolderHierarchySupport: true,
        ...(filterString && { filter: filterString }),
        ...radioFilters,
    };

    const handleSort = (sorter: { columnKey: string; order: "ascend" | "descend" }) => {
        setSortConfig({ columnKey: sorter.columnKey, order: sorter.order });
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
    };

    const handlePaginationChange = (_: number, pageSize: number, nextPage: number) => {
        setPagination({ currentPage: nextPage, pageSize });
    };

    const handleFilterChange = (filterChanges: Partial<IFoldersFilters>) => {
        if (filterChanges.filter) {
            const match = filterChanges.filter.match(FILTER_PATTERNS.FOLDER_CONTAINS);
            setSearchTerm(match ? match[1] : "");
        }
        if (filterChanges.keys) {
            setCurrentDomain(filterChanges.keys);
        }
        if (filterChanges.isAll || filterChanges.isNew || filterChanges.isTrending) {
            setRadioFilters({
                isAll: !!filterChanges.isAll,
                isNew: !!filterChanges.isNew,
                isTrending: !!filterChanges.isTrending,
            });
        }
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
    };

    return {
        currentDomain,
        setCurrentDomain,
        searchTerm,
        filters,
        sortConfig,
        radioFilters,
        handleSort,
        handleFilterChange,
        tablePagination: {
            total: 0,
            pageSize: pagination.pageSize,
            current: pagination.currentPage,
            onChange: handlePaginationChange,
        },
    };
};
